import { Chip } from "@mui/material";
import {
  BooleanInput,
  ChipField,
  Datagrid,
  FunctionField,
  List,
  ReferenceField,
  SelectInput,
  TextField,
  usePermissions,
  type RaRecord,
} from "react-admin";
import { Navigate } from "react-router-dom";

import type { PermissionRecord } from "@/types/permissions";
import { formatDisplayDateTime, formatListDate } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";

import {
  LEAVE_STATUS_CHOICES,
  LEAVE_TYPE_CHOICES,
  leaveStatusChipColor,
  leaveTypeLabel,
  truncateLeaveRemarks,
} from "./leave-request-helpers";

const leaveListFilters = [
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={LEAVE_STATUS_CHOICES}
    alwaysOn
    emptyText="All"
  />,
  <SelectInput
    key="leaveType"
    source="leaveType"
    label="Type"
    choices={LEAVE_TYPE_CHOICES}
    alwaysOn
    emptyText="All"
  />,
  <BooleanInput
    key="pendingAdjustment"
    source="pendingAdjustment"
    label="Adjustment pending"
    alwaysOn
  />,
];

const EmployeeLeaveRequestList = () => {
  const { permissions } = usePermissions<PermissionRecord>();
  // Resource is also registered for portal show deep-links; list stays office-only.
  if (!hasPermission(permissions, "employees.view")) {
    return <Navigate to="/myAttendance" replace />;
  }

  return (
    <List sort={{ field: "createdAt", order: "DESC" }} filters={leaveListFilters} perPage={25}>
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <ReferenceField source="employeeId" reference="employees" link="show">
          <TextField source="name" />
        </ReferenceField>
        <FunctionField
          label="Type"
          source="leaveType"
          render={(record: RaRecord) => leaveTypeLabel(record?.leaveType as string)}
        />
        <FunctionField
          label="From"
          source="startDate"
          render={(record: RaRecord) => formatListDate(record?.startDate, { empty: "—" })}
        />
        <FunctionField
          label="To"
          source="endDate"
          render={(record: RaRecord) => formatListDate(record?.endDate, { empty: "—" })}
        />
        <FunctionField
          label="Status"
          source="status"
          render={(record: RaRecord) => (
            <>
              <ChipField
                source="status"
                record={record}
                color={leaveStatusChipColor(record?.status as string)}
                size="small"
              />
              {record?.pendingAdjustment ? (
                <Chip label="Adjustment pending" color="warning" size="small" sx={{ ml: 0.5 }} />
              ) : null}
            </>
          )}
        />
        <TextField source="source" />
        <FunctionField
          label="Approved by"
          source="reviewedByName"
          render={(record: RaRecord) => (record?.reviewedByName as string) || "—"}
        />
        <FunctionField
          label="Remarks"
          source="reviewRemarks"
          render={(record: RaRecord) => truncateLeaveRemarks(record?.reviewRemarks as string)}
        />
        <FunctionField
          label="Created"
          sortBy="createdAt"
          render={(record: RaRecord) => formatDisplayDateTime(record?.createdAt, { empty: "—" })}
        />
      </Datagrid>
    </List>
  );
};

export default EmployeeLeaveRequestList;
